import { LIMITS } from '@konvoi/shared';
import { useEffect, useState } from 'react';

import { navigate } from '../App.js';
import { api, ApiError, type TourPreview } from '../lib/api.js';
import {
  deviceId,
  loadProfile,
  saveProfile,
  saveSession,
  webSocketUrlForOrigin,
} from '../lib/session.js';

/**
 * Beitreten.
 *
 * Höchstens drei Schritte: Link öffnen, Name und Fahrzeug eintragen, mitfahren.
 * Wer schon einmal dabei war, findet seine Angaben vorbelegt und braucht nur
 * einen Tipp.
 */
export function JoinPage({ code }: { code: string | null }): React.JSX.Element {
  const [eingegebenerCode, setEingegebenerCode] = useState('');
  const [ausfahrt, setAusfahrt] = useState<TourPreview | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [läuft, setLäuft] = useState(false);

  const gespeichert = loadProfile();
  const [name, setName] = useState(gespeichert?.displayName ?? '');
  const [kennzeichen, setKennzeichen] = useState(gespeichert?.licensePlate ?? '');
  const [fahrzeug, setFahrzeug] = useState(gespeichert?.vehicleLabel ?? '');
  const [baujahr, setBaujahr] = useState(
    gespeichert?.vehicleYear === null || gespeichert?.vehicleYear === undefined
      ? ''
      : String(gespeichert.vehicleYear),
  );

  useEffect(() => {
    if (code === null) {
      return;
    }
    setFehler(null);
    api
      .tourByCode(code)
      .then(setAusfahrt)
      .catch((error: unknown) => {
        setFehler(
          error instanceof ApiError ? error.message : 'Diese Ausfahrt ließ sich nicht finden.',
        );
      });
  }, [code]);

  // Schritt eins: der Code, falls die Seite ohne Link geöffnet wurde.
  if (code === null) {
    return (
      <form
        className="seite"
        onSubmit={(event) => {
          event.preventDefault();
          navigate(`/beitreten/${eingegebenerCode.replace(/\D/g, '')}`);
        }}
      >
        <h1>Ausfahrt beitreten</h1>
        <p>Gib den sechsstelligen Code ein, den der Organisator durchgegeben hat.</p>

        <label className="feld">
          <span>Beitrittscode</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9 ]*"
            autoComplete="off"
            value={eingegebenerCode}
            onChange={(event) => {
              setEingegebenerCode(event.target.value);
            }}
            required
          />
        </label>

        <button
          type="submit"
          className="knopf knopf--wichtig"
          disabled={eingegebenerCode.replace(/\D/g, '').length !== 6}
        >
          Weiter
        </button>

        <button
          type="button"
          className="knopf knopf--leise"
          onClick={() => {
            navigate('/');
          }}
        >
          Zurück
        </button>
      </form>
    );
  }

  async function mitfahren(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (code === null) {
      return;
    }

    setFehler(null);
    setLäuft(true);

    const jahr = baujahr.trim() === '' ? null : Number.parseInt(baujahr, 10);

    try {
      const angaben = {
        displayName: name.trim(),
        licensePlate: kennzeichen.trim(),
        // Leer eingegeben heißt: nicht angegeben.
        vehicleLabel: fahrzeug.trim() === '' ? null : fahrzeug.trim(),
        vehicleYear: Number.isNaN(jahr) ? null : jahr,
      };

      const ergebnis = await api.join(code, { deviceId: deviceId(), ...angaben });

      saveProfile(angaben);

      saveSession({
        tourId: ergebnis.tour.id,
        participantId: ergebnis.you.id,
        sessionToken: ergebnis.sessionToken,
        webSocketUrl: webSocketUrlForOrigin(),
        joinCode: code,
        tourName: ergebnis.tour.name,
        role: ergebnis.you.role,
      });

      navigate(`/fahrt/${ergebnis.tour.id}`);
    } catch (error) {
      setFehler(error instanceof ApiError ? error.message : 'Der Beitritt hat nicht geklappt.');
      setLäuft(false);
    }
  }

  return (
    <form className="seite" onSubmit={(event) => void mitfahren(event)}>
      <h1>{ausfahrt?.tour.name ?? 'Ausfahrt beitreten'}</h1>

      {ausfahrt !== null && (
        <p>
          {ausfahrt.participantCount === 0
            ? 'Noch niemand dabei — du wärst das erste Fahrzeug und führst damit die Kolonne an.'
            : `${ausfahrt.participantCount} ${ausfahrt.participantCount === 1 ? 'Fahrzeug ist' : 'Fahrzeuge sind'} schon dabei.`}
        </p>
      )}

      {/*
        Der Fehler holt den Blick zu sich.

        Er steht oben im Formular, die Schaltfläche „Mitfahren" ganz unten —
        wer dort tippt, sieht ihn nicht und hält die App für tot. Genau so ist
        am 14.08.2026 ein abgewiesener Beitritt als „die Schaltfläche tut
        nichts" erschienen.
      */}
      {fehler !== null && (
        <p
          className="hinweis hinweis--alarm"
          ref={(knoten) => {
            knoten?.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }}
        >
          {fehler}
        </p>
      )}

      {/*
        Die Beispiele in den Feldern sind bewusst erfunden.

        Stand dort der Name oder das Kennzeichen von jemandem, der die App
        gebaut hat, sähe es aus wie eine bereits gemachte Angabe — und der
        Fahrer fragte sich, ob da schon jemand eingetragen ist. Ein Beispiel
        muss als solches zu erkennen sein.
      */}
      <label className="feld">
        <span>Dein Name</span>
        <input
          type="text"
          value={name}
          maxLength={LIMITS.displayNameMaxLength}
          required
          autoComplete="name"
          placeholder="Max Mustermann"
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
      </label>

      {/*
        Das Kennzeichen ist Pflicht — es ist das Einzige, woran ein Fahrzeug in
        der Kolonne zweifelsfrei zu erkennen ist. Namen wiederholen sich, und
        rote 911 gibt es auf einer Oldtimer-Ausfahrt mehrere.
      */}
      <label className="feld">
        <span>Kennzeichen</span>
        <input
          type="text"
          value={kennzeichen}
          maxLength={LIMITS.licensePlateMaxLength}
          required
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          // Kein echter Zulassungsbezirk: „AB-CD" gibt es so nicht und
          // zeigt trotzdem die Form, in der ein Kennzeichen erwartet wird.
          placeholder="AB-CD 123"
          onChange={(event) => {
            setKennzeichen(event.target.value);
          }}
        />
      </label>

      <label className="feld">
        <span>Fahrzeug (kann leer bleiben)</span>
        <input
          type="text"
          value={fahrzeug ?? ''}
          maxLength={LIMITS.vehicleLabelMaxLength}
          autoComplete="off"
          placeholder="Porsche 911"
          onChange={(event) => {
            setFahrzeug(event.target.value);
          }}
        />
      </label>

      <label className="feld">
        <span>Baujahr (kann leer bleiben)</span>
        <input
          type="text"
          inputMode="numeric"
          value={baujahr}
          maxLength={4}
          autoComplete="off"
          placeholder="1973"
          onChange={(event) => {
            setBaujahr(event.target.value);
          }}
        />
      </label>

      <div className="hinweis datenschutz">
        <strong>Was geteilt wird:</strong> Während der Ausfahrt sehen die anderen Teilnehmer deine
        Position, deinen Namen, dein Kennzeichen und dein Fahrzeug. Sonst niemand. Vierundzwanzig Stunden nach dem Ende
        der Ausfahrt werden alle Positionsdaten gelöscht. Du kannst dich jederzeit unsichtbar
        schalten oder die Ausfahrt verlassen.
      </div>

      <button type="submit" className="knopf knopf--wichtig" disabled={läuft || ausfahrt === null}>
        {läuft ? 'Einen Augenblick …' : 'Mitfahren'}
      </button>

      {/*
        Ein Weg zurück, auch wenn der Beitritt scheitert.
        Ohne ihn war diese Seite eine Sackgasse: Wer sich im Code vertan hatte
        oder auf einen Fehler lief, kam nur noch heraus, indem er die App
        schloss und neu startete.
      */}
      <button
        type="button"
        className="knopf knopf--leise"
        disabled={läuft}
        onClick={() => {
          navigate('/beitreten');
        }}
      >
        Zurück
      </button>

      <p className="datenschutz">
        Im nächsten Schritt fragt der Browser nach der Standortfreigabe. Ohne sie sehen dich die
        anderen nicht.
      </p>
    </form>
  );
}
